import { type FocusEvent, type ReactNode, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { PAY_METHOD } from 'constants/payment';
import {
  useAmount,
  useMainNavigate,
  usePayment,
  useSelectCard,
  useTranslate,
  useValidate,
} from 'hooks';
import { useAppSelector } from 'store/hooks';
import { useGetCertQuery } from 'store/payment/service';
import { ButtonKinds } from 'types/button';
import { ecryptedWithJwkPublickKey, getOfferHrefWithVersion } from 'utils';
import { SelectCard } from 'views/components/bet-city/select-card';
import { Button, Input } from 'views/components/bet-city/ui';
import {
  type CardFieldsType,
  CvvInput,
  DateInput,
  NameInput,
  PanInput,
} from 'views/shared/card-details';

import { ApplePay } from '../../base/apple-pay';
import { GooglePay } from '../../base/google-pay';

const CardForm = () => {
  const { paymentData, isPayment, amount, acquiring } = useAppSelector((state) => state.payment);
  const { locale } = useAppSelector((state) => state.app);
  const { data: cert } = useGetCertQuery(null);
  const [isSaveCard, setIsSaveCard] = useState(true);
  const { fetchSendPayment, isLoading } = usePayment();
  const { addCard, removeCard, cards, selectedCard, setSelectedCard } = useSelectCard('fullCards');
  const [focusName, setFocusName] = useState<keyof CardFieldsType>('pan');
  const { formattedFullAmount, currentAmountForPayment, isValidCurrentAmount } = useAmount();
  const { goToReturnUrl } = useMainNavigate();

  const isExpressPayment = useMemo(() => {
    if (paymentData) {
      return paymentData.acquirings.some(
        (item) => item.formType === PAY_METHOD.GOOGLE_PAY || item.formType === PAY_METHOD.APPLE_PAY,
      );
    }

    return false;
  }, [paymentData]);

  const methods = useForm<CardFieldsType>({
    mode: 'onChange',
    defaultValues: isPayment
      ? {
          pan: '',
          month: '',
          year: '',
          cvv: '',
          name: '',
        }
      : { pan: '' },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, dirtyFields, isValid },
    setFocus,
    setError,
    resetField,
    reset,
    watch,
    trigger,
    clearErrors,
  } = methods;

  const month = watch('month');
  const year = watch('year');
  const cardNumber = watch('pan');

  const { t } = useTranslate();
  const { validateCardDate } = useValidate();

  useEffect(() => {
    if (selectedCard.newCard && dirtyFields.month) {
      const validity = `${month}/${year}`;

      const valid = validateCardDate(validity);

      if (typeof valid === 'string') {
        setError('month', {
          message: valid,
        });
        setError('year', {
          message: valid,
        });
      } else {
        clearErrors('month');
        clearErrors('year');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year, selectedCard]);

  const fields: (keyof CardFieldsType)[] = ['pan', 'month', 'year', 'cvv', 'name'];

  const findIsNextInput = (nameInput: keyof CardFieldsType): keyof CardFieldsType => {
    if (!errors[nameInput] && dirtyFields[nameInput]) {
      const indexField = fields.findIndex((item) => item === nameInput);

      if (indexField !== -1) {
        return findIsNextInput(fields[indexField + 1]);
      }

      return fields[indexField + 1];
    }

    return nameInput;
  };

  useEffect(() => {
    const indexField = fields.findIndex((item) => item === focusName);

    const nextInput = fields[indexField + 1];

    if (nextInput && !errors[focusName] && dirtyFields[focusName]) {
      setFocus(findIsNextInput(nextInput));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors.pan, errors.month, errors.year, errors.cvv]);

  useEffect(() => {
    if (!selectedCard.newCard) {
      reset();
      setIsSaveCard(false);
    }

    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCard]);

  const onHandleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setFocusName(e.target.name as keyof CardFieldsType);
  };

  const sendPayment = async (data: CardFieldsType) => {
    if (cert) {
      const cardData = isPayment
        ? {
            cvv: data.cvv,
            validity: `${data.month}/${data.year}`,
            pan: data.pan.replace(/\s/g, ''),
          }
        : {
            pan: data.pan.replace(/\s/g, ''),
          };

      const crypt = await ecryptedWithJwkPublickKey(cert, JSON.stringify(cardData));

      const payData = {
        phone: '',
        email: '',
        description: '',
        ID: paymentData?.ID,
        acquiringID: acquiring?.ID,
        amount: currentAmountForPayment,
        bankBin: cardData.pan ? cardData.pan.slice(0, 6) : null,
        cardData: crypt,
      };

      if (!selectedCard.newCard && selectedCard.crypt && selectedCard.bankBin) {
        payData.cardData = selectedCard.crypt;
        payData.bankBin = selectedCard.bankBin;
      }

      await fetchSendPayment(payData).then(() => {
        if (crypt && isSaveCard) {
          addCard({
            crypt,
            pan: cardData.pan,
            name: data.name,
          });
        }
      });
    }
  };

  const renderExpressPaymentBtns = () => {
    return (
      <div className="flex gap-4">
        {paymentData?.acquirings.map((item) => {
          if (item.formType === PAY_METHOD.GOOGLE_PAY) {
            return <GooglePay key={item.ID} acquiringID={item.ID} />;
          }

          if (item.formType === PAY_METHOD.APPLE_PAY) {
            return <ApplePay key={item.ID} acquiringID={item.ID} />;
          }

          return null;
        })}
      </div>
    );
  };

  const renderLink = (chunks: ReactNode) => {
    const href = getOfferHrefWithVersion(locale);

    return (
      <a href={href} rel="noreferrer" target="_blank" className="underline hover:no-underline">
        {chunks}
      </a>
    );
  };

  return paymentData ? (
    <FormProvider {...methods}>
      <form className="flex flex-col gap-5 md:gap-3">
        <div className="flex justify-between">
          <dl className="text-base md:text-xl leading-5 font-normal text-betCity-gray-100 text-left">
            <dt>{t('betCity.payment-order')}</dt>
            <dd className="text-black font-semibold leading-5 mt-2 md:mt-3">№{paymentData.ID}</dd>
          </dl>
          <dl className="text-base md:text-xl leading-5 font-normal text-betCity-gray-100 text-right">
            <dt>{t('betCity.payment-amount')}</dt>
            <dd className="text-black font-semibold leading-5 mt-2 md:mt-3">
              {formattedFullAmount}
            </dd>
          </dl>
        </div>
        {isExpressPayment && <div>{renderExpressPaymentBtns()}</div>}
        {isExpressPayment && acquiring && (
          <div className="flex items-center justify-between gap-8 text-betCity-gray-100">
            <div className="max-w-[200px] w-full h-[1px] bg-betCity-gray-100" />
            <span>{t('betCity.or')}</span>
            <div className="max-w-[200px] w-full h-[1px] bg-betCity-gray-100" />
          </div>
        )}
        {acquiring && (
          <div className="flex flex-col gap-5 md:gap-3">
            <div className="flex flex-col gap-[22px] md:gap-3">
              <h3 className="text-lg md:text-xl text-black font-semibold leading-4 md:leading-[30px]">
                {acquiring.title}
              </h3>
              {cards?.length ? (
                <div>
                  <SelectCard
                    handleDelete={(pan) => removeCard(pan)}
                    cards={cards}
                    selectedCard={selectedCard}
                    setSelectedCard={setSelectedCard}
                  />
                </div>
              ) : null}
              <div className="flex flex-col gap-5 md:gap-3">
                <PanInput
                  amount={amount}
                  cardNumber={cardNumber}
                  control={control}
                  onHandleFocus={onHandleFocus}
                  paymentData={paymentData}
                  resetField={resetField}
                  selectedCard={selectedCard}
                  InputComponent={Input}
                />
                {isPayment ? (
                  <>
                    <div className="flex gap-3 lg:mt-0">
                      <DateInput
                        amount={amount}
                        control={control}
                        errors={errors}
                        onHandleFocus={onHandleFocus}
                        paymentData={paymentData}
                        selectedCard={selectedCard}
                        InputComponent={Input}
                      />
                      <CvvInput
                        amount={amount}
                        control={control}
                        onHandleFocus={onHandleFocus}
                        paymentData={paymentData}
                        selectedCard={selectedCard}
                        InputComponent={Input}
                      />
                    </div>
                    <NameInput
                      amount={amount}
                      control={control}
                      onHandleFocus={onHandleFocus}
                      paymentData={paymentData}
                      selectedCard={selectedCard}
                      InputComponent={Input}
                    />
                  </>
                ) : null}
                <Button
                  onClick={handleSubmit(sendPayment)}
                  disabled={!isValid || !isValidCurrentAmount}
                  isLoading={isLoading}
                >
                  {isPayment ? t('betCity.pay') : t('betCity.payout')}
                </Button>
                <Button
                  kind={ButtonKinds.secondary}
                  onClick={() => goToReturnUrl(paymentData.returnURL)}
                >
                  {t('betCity.cancel')}
                </Button>
                <div className="text-[12px] md:text-sm">
                  <p className="text-betCity-gray-300">
                    {t('betCity.terms-of-service', { a: renderLink })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
    </FormProvider>
  ) : null;
};

export { CardForm };
